import React from "react";
import styled from "@emotion/styled";
import { NavLink } from "react-router-dom";
import { Helmet } from "react-helmet-async";

import {
  Box,
  Breadcrumbs as MuiBreadcrumbs,
  Divider as MuiDivider,
  Grid,
  Link,
  Typography as MuiTypography,
  List,
  ListItem,
} from "@mui/material";
import { spacing } from "@mui/system";

const Divider = styled(MuiDivider)(spacing);

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);

const Typography = styled(MuiTypography)(spacing);

function PoliciesAndProcedures() {
  return (
    <React.Fragment>
      <Helmet title="Policies and Procedures" />

      <Grid container spacing={6} justifyContent="center">
        <Grid item xs={12} lg={9} xl={7}>
          <Typography variant="h2" gutterBottom display="inline">
            Technical Safeguards
          </Typography>

          <Breadcrumbs aria-label="Breadcrumb" mt={2}>
            <Link component={NavLink} to="/">
              Dashboard
            </Link>
            <Typography>Technical Safeguards</Typography>
          </Breadcrumbs>

          <Divider my={6} />

          <Typography variant="body1" paragraph>
            This section of the documentation focuses on the Technical Safeguards as defined by the Health Insurance
            Portability and Accountability Act (HIPAA) to ensure the confidentiality, integrity, and security of
            electronic protected health information (ePHI). Technical safeguards are critical in protecting ePHI from
            unauthorized access, alteration, and deletion. Our SaaS medical application incorporates advanced security
            measures and technologies to adhere to these safeguards, which are essential for maintaining the trust and
            confidence of our users and patients. The following outlines the key components and practices within our
            technical safeguards framework.
          </Typography>

          <Typography variant="h5" gutterBottom>
            Technical Safeguards Overview
          </Typography>
          <List>
            <ListItem alignItems="flex-start">
              <Box display="flex" flexDirection="column">
                <Typography component="span">
                  <b>Access Control:</b> Implements policies and procedures that grant access to ePHI only to those
                  persons or software programs that have been granted access rights. This includes the establishment of
                  unique user identifiers, emergency access procedures, automatic logoff, and encryption and decryption
                  mechanisms.
                </Typography>
              </Box>
            </ListItem>
            <ListItem alignItems="flex-start">
              <Box display="flex" flexDirection="column">
                <Typography component="span">
                  <b>Audit Controls:</b> We deploy hardware, software, and procedural mechanisms that record and examine
                  activity in information systems containing or using ePHI. This helps in tracking access and other
                  actions that could affect the integrity and confidentiality of ePHI.
                </Typography>
              </Box>
            </ListItem>
            <ListItem alignItems="flex-start">
              <Box display="flex" flexDirection="column">
                <Typography component="span">
                  <b>Integrity Controls:</b> Procedures and mechanisms are in place to ensure that ePHI is not
                  improperly altered or destroyed. Digital mechanisms ensure the authenticity and integrity of ePHI,
                  safeguarding it against unauthorized alterations or destruction.
                </Typography>
              </Box>
            </ListItem>
            <ListItem alignItems="flex-start">
              <Box display="flex" flexDirection="column">
                <Typography component="span">
                  <b>Transmission Security:</b> Ensures that ePHI transmitted over an electronic network is protected
                  against unauthorized access, tampering, or interceptions. This includes integrity controls and
                  encryption during transmission.
                </Typography>
              </Box>
            </ListItem>
          </List>

          <Typography variant="h5" gutterBottom>
            Objectives
          </Typography>
          <List>
            <ListItem>
              To implement stringent access control measures that restrict access to ePHI solely to individuals and
              entities with the right to access.
            </ListItem>
            <ListItem>
              To continuously audit and review system activities, thereby ensuring that any unauthorized access or
              activities can be identified and mitigated promptly.
            </ListItem>
            <ListItem>
              To uphold the integrity of ePHI, ensuring that information is not altered or destroyed in an unauthorized
              manner.
            </ListItem>
            <ListItem>
              To secure ePHI during electronic transmission across networks, preventing unauthorized interception,
              modification, or access.
            </ListItem>
          </List>
        </Grid>
      </Grid>
    </React.Fragment>
  );
}

export default PoliciesAndProcedures;
